import { createSlice, nanoid } from "@reduxjs/toolkit";
import {
  createShreddingTemplate,
  deleteShreddingTemplate,
  duplicateShreddingTemplate,
  fetchShreddingTemplate,
  fetchShreddingTemplates,
  importShreddingTemplate,
  lockShreddingTemplate,
  unlockShreddingTemplate,
  updateShreddingTemplate,
} from "slices/settings/actions/shreddingTemplates";
import {
  addColumn,
  deleteGroup,
  duplicateColumn,
  duplicateGroup,
  groupColumns,
  removeColumn,
  removeColumns,
  reorderColumns,
  setColumnWidth,
  toggleCoreColumnHidden,
  ungroupColumns,
  updateColumn,
  updateGroup,
} from "slices/settings/shreddingTemplateDraftLayout";
import { SHREDDING_TEMPLATE_ERROR_CODES } from "slices/settings/shreddingTemplatesValidation";

export const DEFAULT_FETCH_ERROR =
  "The Shred Templates could not be loaded. Please try again.";

export const DEFAULT_DETAIL_ERROR =
  "This template could not be loaded. Please try again.";

export const initialState = {
  templates: null,
  isLoading: false,
  isCreating: false,
  error: null,
  detail: { isLoading: false, error: null },
  edit: null,
};

const startEditing = (template) => ({
  templateId: template.id,
  original: template,
  draft: template,
  isSaving: false,
  nameError: null,
});

const setLockedBy = (state, id, lockedBy) => {
  state.templates = (state.templates ?? []).map((template) =>
    template.id === id ? { ...template, lockedBy } : template
  );
};

const isEditOf = (state, templateId) => state.edit?.templateId === templateId;

const upsertTemplate = (state, template) => {
  const templates = state.templates ?? [];
  const exists = templates.some(({ id }) => id === template.id);
  state.templates = exists
    ? templates.map((existing) =>
        existing.id === template.id ? template : existing
      )
    : [...templates, template];
};

const shreddingTemplatesSlice = createSlice({
  name: "shreddingTemplates",
  initialState,
  reducers: {
    updateShreddingTemplateDraft: (state, { payload }) => {
      if (!state.edit) {
        return;
      }
      state.edit.draft = { ...state.edit.draft, ...payload };
      if ("name" in payload) {
        state.edit.nameError = null;
      }
    },
    addShreddingTemplateDraftColumn: {
      reducer: (state, { payload }) => {
        if (!state.edit) {
          return;
        }
        state.edit.draft = addColumn(
          state.edit.draft,
          payload.column,
          payload.intoGroupKey
        );
      },
      prepare: (column, intoGroupKey = null) => ({
        payload: {
          column: { ...column, key: column.key ?? nanoid() },
          intoGroupKey,
        },
      }),
    },
    updateShreddingTemplateDraftColumn: (state, { payload }) => {
      if (!state.edit) {
        return;
      }
      state.edit.draft = updateColumn(
        state.edit.draft,
        payload.columnKey,
        payload.patch
      );
    },
    removeShreddingTemplateDraftColumn: (state, { payload }) => {
      if (!state.edit) {
        return;
      }
      state.edit.draft = removeColumn(state.edit.draft, payload);
    },
    reorderShreddingTemplateDraftColumns: (state, { payload }) => {
      if (!state.edit) {
        return;
      }
      state.edit.draft = reorderColumns(state.edit.draft, payload);
    },
    setShreddingTemplateDraftColumnWidth: (state, { payload }) => {
      if (!state.edit) {
        return;
      }
      state.edit.draft = setColumnWidth(
        state.edit.draft,
        payload.previewColumnId,
        payload.width
      );
    },
    groupShreddingTemplateDraftColumns: {
      reducer: (state, { payload }) => {
        if (!state.edit) {
          return;
        }
        state.edit.draft = groupColumns(state.edit.draft, payload);
      },
      prepare: (group) => ({ payload: { ...group, key: nanoid() } }),
    },
    updateShreddingTemplateDraftGroup: (state, { payload }) => {
      if (!state.edit) {
        return;
      }
      state.edit.draft = updateGroup(
        state.edit.draft,
        payload.groupKey,
        payload.patch
      );
    },
    ungroupShreddingTemplateDraftColumns: (state, { payload }) => {
      if (!state.edit) {
        return;
      }
      state.edit.draft = ungroupColumns(state.edit.draft, payload);
    },
    deleteShreddingTemplateDraftGroup: (state, { payload }) => {
      if (!state.edit) {
        return;
      }
      state.edit.draft = deleteGroup(state.edit.draft, payload);
    },
    duplicateShreddingTemplateDraftGroup: {
      reducer: (state, { payload }) => {
        if (!state.edit) {
          return;
        }
        state.edit.draft = duplicateGroup(
          state.edit.draft,
          payload.groupKey,
          payload.copyKey
        );
      },
      prepare: (groupKey) => ({ payload: { groupKey, copyKey: nanoid() } }),
    },
    duplicateShreddingTemplateDraftColumn: {
      reducer: (state, { payload }) => {
        if (!state.edit) {
          return;
        }
        state.edit.draft = duplicateColumn(
          state.edit.draft,
          payload.columnKey,
          payload.copyKey
        );
      },
      prepare: (columnKey) => ({ payload: { columnKey, copyKey: nanoid() } }),
    },
    removeShreddingTemplateDraftColumns: (state, { payload }) => {
      if (!state.edit) {
        return;
      }
      state.edit.draft = removeColumns(state.edit.draft, payload);
    },
    toggleDraftCoreColumnHidden: (state, { payload }) => {
      if (!state.edit) {
        return;
      }
      state.edit.draft = toggleCoreColumnHidden(state.edit.draft, payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchShreddingTemplates.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchShreddingTemplates.fulfilled, (state, action) => {
        state.isLoading = false;
        state.templates = action.payload ?? [];
      })
      .addCase(fetchShreddingTemplates.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message ?? DEFAULT_FETCH_ERROR;
      })
      .addCase(createShreddingTemplate.pending, (state) => {
        state.isCreating = true;
      })
      .addCase(createShreddingTemplate.fulfilled, (state, action) => {
        state.isCreating = false;
        upsertTemplate(state, action.payload);
      })
      .addCase(createShreddingTemplate.rejected, (state) => {
        state.isCreating = false;
      })
      .addCase(duplicateShreddingTemplate.fulfilled, (state, action) => {
        upsertTemplate(state, action.payload);
      })
      .addCase(importShreddingTemplate.fulfilled, (state, action) => {
        upsertTemplate(state, action.payload);
      })
      .addCase(deleteShreddingTemplate.fulfilled, (state, action) => {
        state.templates = (state.templates ?? []).filter(
          ({ id }) => id !== action.payload
        );
      })
      .addCase(deleteShreddingTemplate.rejected, (state, action) => {
        if (
          action.payload?.errorCode ===
          SHREDDING_TEMPLATE_ERROR_CODES.templateLocked
        ) {
          setLockedBy(state, action.meta.arg, action.payload.details);
        }
      })
      .addCase(fetchShreddingTemplate.pending, (state) => {
        state.detail = { isLoading: true, error: null };
      })
      .addCase(fetchShreddingTemplate.fulfilled, (state, action) => {
        state.detail = { isLoading: false, error: null };
        upsertTemplate(state, action.payload);
      })
      .addCase(fetchShreddingTemplate.rejected, (state, action) => {
        state.detail = {
          isLoading: false,
          error: action.payload?.message ?? DEFAULT_DETAIL_ERROR,
        };
      })
      .addCase(lockShreddingTemplate.fulfilled, (state, action) => {
        upsertTemplate(state, action.payload);
        state.edit = startEditing(action.payload);
      })
      .addCase(lockShreddingTemplate.rejected, (state, action) => {
        if (
          action.payload?.errorCode ===
          SHREDDING_TEMPLATE_ERROR_CODES.templateLocked
        ) {
          setLockedBy(state, action.meta.arg, action.payload.details);
        }
      })
      .addCase(unlockShreddingTemplate.pending, (state, action) => {
        state.edit = null;
        setLockedBy(state, action.meta.arg, null);
      })
      .addCase(updateShreddingTemplate.pending, (state, action) => {
        if (isEditOf(state, action.meta.arg.id)) {
          state.edit.isSaving = true;
        }
      })
      .addCase(updateShreddingTemplate.fulfilled, (state, action) => {
        upsertTemplate(state, action.payload);
        if (isEditOf(state, action.payload.id)) {
          state.edit = null;
        }
      })
      .addCase(updateShreddingTemplate.rejected, (state, action) => {
        if (!isEditOf(state, action.meta.arg.id)) {
          return;
        }
        state.edit.isSaving = false;
        if (
          action.payload?.errorCode ===
          SHREDDING_TEMPLATE_ERROR_CODES.duplicateName
        ) {
          state.edit.nameError = action.payload.message;
        }
      });
  },
});

export const {
  addShreddingTemplateDraftColumn,
  deleteShreddingTemplateDraftGroup,
  duplicateShreddingTemplateDraftColumn,
  duplicateShreddingTemplateDraftGroup,
  groupShreddingTemplateDraftColumns,
  removeShreddingTemplateDraftColumns,
  ungroupShreddingTemplateDraftColumns,
  updateShreddingTemplateDraftGroup,
  removeShreddingTemplateDraftColumn,
  reorderShreddingTemplateDraftColumns,
  setShreddingTemplateDraftColumnWidth,
  toggleDraftCoreColumnHidden,
  updateShreddingTemplateDraft,
  updateShreddingTemplateDraftColumn,
} = shreddingTemplatesSlice.actions;

export default shreddingTemplatesSlice.reducer;
